'use client'

import {ReactNode, forwardRef, InputHTMLAttributes, PropsWithChildren, useContext, useRef} from 'react'
import {FormContext} from '@local/stores/contexts'
import {cx} from '@local/utils/cx'

export interface RadioButtonProps extends InputHTMLAttributes<HTMLInputElement> {
	children: ReactNode,
	className?: string,
	inputClassName?: string,
	labelClassName?: string,
	checkClassName?: string,
	required?: boolean,
	disabled?: boolean,
	active?: boolean,
	theme?: 'light' | 'dark',
	error?: boolean,
	asButton?: boolean,
	canDeselect?: boolean,
	onClick?: () => void
}

export const RadioButton = forwardRef<HTMLInputElement, PropsWithChildren<RadioButtonProps>>(({
	name,
	id,
	children,
	className,
	inputClassName,
	labelClassName,
	checkClassName,
	required = false,
	disabled = false,
	theme = 'light',
	error = false,
	canDeselect = false,
	...props
}: RadioButtonProps, ref) => {
	const {hasValidation} = useContext(FormContext)

	const localRef = useRef<HTMLElement>(null)
	const setRefs = (node) => {
		localRef.current = node
		if (typeof ref === 'function') {
			ref(node)
		} else if (ref) {
			ref.current = node
		}
	}

	const handleClick = (e) => {
		if (canDeselect && localRef.current) {
			e.preventDefault()
			e.stopPropagation()
			localRef.current.checked = !localRef.current.checked
		}
	}

	return (
		<label
			onClick={handleClick}
			className={cx(
				'relative inline-flex items-baseline gap-8',
				'font-light text-12 cursor-pointer text-black text-left',
				disabled && 'cursor-auto opacity-60 pointer-events-none',
				className,
			)}
			htmlFor={id || name}>
			<input
				ref={setRefs}
				type='radio'
				id={id || name}
				name={name || id}
				required={required}
				className={cx(
					'absolute pointer-events-none opacity-0 peer',
					inputClassName,
				)}
				{...props} />
			<i
				className={cx(
					'relative flex items-center justify-center rounded-full',
					theme === 'dark' ? 'text-white border-grey-2 peer-focus-visible:border-grey-1' : 'text-black peer-focus-visible:border-grey-3',
					'shrink-0 w-[16px] h-[16px] border peer-checked:[&>span]:opacity-100',
					'transition-[background-color,border-color] duration-300 ease-quart-out',
					theme === 'light' ? 'border-grey-1 peer-checked:bg-white peer-checked:border-black' : 'peer-checked:bg-white',
					hasValidation && 'peer-invalid:border-red',
					checkClassName,
				)} aria-hidden>
				<span className={cx(
					'w-12 h-12 rounded-full m-auto opacity-0',
					'transition-opacity duration-300 ease-quart-out',
					theme === 'dark' ? 'bg-white' : 'bg-black',
				)} />
			</i>
			<span className={cx(
				'transition-color duration-300 ease-quart-out',
				error && 'text-red',
				hasValidation && 'peer-invalid:text-red',
				labelClassName,
			)}>
				{children}
			</span>
		</label>
	)
})
